<template>
    <div class="nav-item user-info-nav-item">
        <SkyPopoverMenu
            v-model:active="isOpen"
            class="user-info-menu">
            <template #trigger="{ attrs: popoverAttrs, handlers }">
                <!-- This element must be an <a> instead of a <SkyButton> because SkyButton requires its contents
                     to be displayed in a span, and <FontAwesomeLayers> is a div. This creates invalid HTML that
                     fails accessibility audits. -->
                <a
                    href="#"
                    data-test="main-nav-user-info"
                    class="user-info-menu__button trigger"
                    role="button"
                    v-bind="popoverAttrs"
                    :title="config.user.alias"
                    :aria-label="menuTitle"
                    v-on="handlers">
                    <div
                        class="badge-container"
                        data-test="badge-container"
                        aria-hidden="true">
                        <FontAwesomeLayers>
                            <UserAvatar
                                class="user-info-tile__avatar"
                                size="sm"
                                :user="config.user">
                            </UserAvatar>

                            <CxFontAwesomeIcon
                                v-if="config.hasNotifications"
                                action="solidCircle"
                                transform="shrink-1 right-14 up-14"
                                class="fa-badge-brand"
                                data-test="container-notifications-icon">
                            </CxFontAwesomeIcon>
                        </FontAwesomeLayers>

                        <SkyText
                            variant="sm"
                            class="nav-link-base nav-link-brand user-info-menu__user-alias"
                            data-test="user-alias">
                            {{ config.user.alias }}
                        </SkyText>
                        <CxFontAwesomeIcon action="collapseDown"></CxFontAwesomeIcon>
                    </div>
                </a>
            </template>

            <template #menu="{ attrs: menuAttrs }">
                <SkyDropdownMenu
                    :data-test="isOpen ? 'open-menu-user-info-dropdown' : ''"
                    v-bind="menuAttrs"
                    class="user-info-dropdown-menu">
                    <SkyDropdownMenuItem
                        :href="config.urls.profile"
                        data-test="user-menu-profile"
                        v-bind="{
                            ...getPendoAttribute('user-menu-profile'),
                        }">
                        <span
                            v-if="showExpandedMessage"
                            class="sr-only"
                            >{{ expandedMessage }}</span
                        >
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <cx-font-awesome-icon
                                    data-test="profile-icon"
                                    v-bind="iconAttrs"
                                    action="profile">
                                </cx-font-awesome-icon>
                            </SkyIcon>
                        </template>
                        {{ $formatMessage(globalMessages.profile) }}
                    </SkyDropdownMenuItem>

                    <SkyDropdownMenuItem
                        :href="config.urls.notifications"
                        :title="notificationsText"
                        data-test="main-nav-notifications"
                        v-bind="{
                            ...getPendoAttribute('user-menu-notifications'),
                        }"
                        :aria-label="
                            $formatMessage({
                                defaultMessage: 'See Your Notifications',
                                id: 'globals.desktopUserInfoMenu.notificationsAria',
                            })
                        ">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <font-awesome-layers
                                    data-test="notifications-nav-icon"
                                    v-bind="iconAttrs"
                                    class="nav-icon"
                                    fixed-width>
                                    <cx-font-awesome-icon
                                        action="notifications"
                                        data-test="notifications-icon">
                                    </cx-font-awesome-icon>

                                    <cx-font-awesome-icon
                                        v-if="config.hasNotifications"
                                        action="solidCircle"
                                        transform="shrink-6 right-5 up-5"
                                        class="fa-badge-brand"
                                        data-test="main-nav-notifications-are-notifications"></cx-font-awesome-icon>
                                </font-awesome-layers>
                            </SkyIcon>
                        </template>

                        {{ notificationsText }}
                    </SkyDropdownMenuItem>

                    <SkyDropdownMenuItem
                        :href="config.urls.settings"
                        data-test="user-menu-settings"
                        v-bind="{
                            ...getPendoAttribute('user-menu-settings'),
                        }">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <cx-font-awesome-icon
                                    data-test="settings-icon"
                                    v-bind="iconAttrs"
                                    action="settings"></cx-font-awesome-icon>
                            </SkyIcon>
                        </template>

                        {{ $formatMessage(globalMessages.settings) }}
                    </SkyDropdownMenuItem>

                    <SkyDropdownMenuItem
                        v-if="config.leaderDashboard?.enabled"
                        :href="config.leaderDashboard.url"
                        data-test="user-menu-leader-dashboard"
                        v-bind="{
                            ...getPendoAttribute('user-menu-leader-dashboard'),
                        }">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <cx-font-awesome-icon
                                    data-test="leader-dashboard-icon"
                                    v-bind="iconAttrs"
                                    action="leaderDashboard"></cx-font-awesome-icon>
                            </SkyIcon>
                        </template>

                        {{ $formatMessage(globalMessages.leaderDashboard) }}
                    </SkyDropdownMenuItem>

                    <SkyDropdownMenuItem
                        :href="config.urls.welcome"
                        data-test="user-menu-about"
                        v-bind="{ ...getPendoAttribute('user-menu-about') }">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <cx-font-awesome-icon
                                    data-test="about-icon"
                                    v-bind="iconAttrs"
                                    action="help">
                                </cx-font-awesome-icon>
                            </SkyIcon>
                        </template>

                        {{ $formatMessage(globalMessages.getStarted) }}
                    </SkyDropdownMenuItem>
                    <SkyDropdownMenuItem
                        v-if="sparkProgramName && config.urls.spark_url"
                        :href="config.urls.spark_url"
                        :title="
                            $formatMessage(
                                {
                                    defaultMessage: 'Link to {community_name} Spark',
                                    id: 'globals.sparkHeaderLink.linkText',
                                },
                                { community_name: sparkProgramName },
                            )
                        "
                        v-bind="{
                            ...getPendoAttribute('user-menu-spark-url'),
                        }"
                        data-test="user-menu-spark-url"
                        class="spark-link"
                        target="_blank"
                        @click="sparkLinkClick">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <MatchIcon v-bind="iconAttrs"></MatchIcon>
                            </SkyIcon>
                        </template>
                        {{ sparkProgramName }}
                    </SkyDropdownMenuItem>

                    <SkyDropdownMenuItem
                        href="#"
                        data-test="user-menu-sign-out"
                        @click.prevent="logout">
                        <template #icon="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <cx-font-awesome-icon
                                    data-test="sign-out-icon"
                                    v-bind="iconAttrs"
                                    action="signOut"></cx-font-awesome-icon>
                            </SkyIcon>
                        </template>

                        {{ $formatMessage(globalMessages.logOut) }}
                    </SkyDropdownMenuItem>
                </SkyDropdownMenu>
            </template>
        </SkyPopoverMenu>
    </div>
</template>

<script lang="ts">
    import CxFontAwesomeIcon from "@eg_ui/src/components/globals/CxFontAwesomeIcon/CxFontAwesomeIcon.vue";
    import MatchIcon from "@eg_ui/src/components/globals/MatchIcon/MatchIcon.vue";
    import UserAvatar from "@eg_ui/src/components/globals/UserAvatar/UserAvatar.vue";
    import { toggle } from "@eg_ui/src/mixins/globals/toggle";
    import { globalMessages } from "@eg_ui/src/translations/globals";
    import { getPendoAttribute } from "@eg_ui/src/utils/functions";
    import { GLOBAL_EVENTS, globalEventHandler } from "@eg_ui/src/utils/libs";
    import { FontAwesomeLayers } from "@fortawesome/vue-fontawesome";

    import { SkyDropdownMenu, SkyDropdownMenuItem, SkyIcon, SkyPopoverMenu, SkyText } from "@skyline2/vue";

    export interface IDesktopUserInfoMenuLeaderDashboard {
        enabled: boolean;
        url: string;
    }

    export interface IDesktopUserInfoMenuUrls {
        notifications: string;
        profile: string;
        settings: string;
        spark_url?: string | null;
        welcome: string;
    }

    export interface IDesktopUserInfoMenuUser {
        alias: string;
        avatar_url: string | null;
        deactivated: boolean;
        [key: string]: unknown;
    }

    export interface IDesktopUserInfoMenuConfig {
        communityName: string;
        isAuthenticated: boolean;
        urls: IDesktopUserInfoMenuUrls;
        hasNotifications: boolean;
        isAdmin: boolean;
        user: IDesktopUserInfoMenuUser;
        leaderDashboard?: IDesktopUserInfoMenuLeaderDashboard;
        sparkProgramName?: string | null;
    }

    export default {
        components: {
            CxFontAwesomeIcon,
            FontAwesomeLayers,
            MatchIcon,
            SkyDropdownMenu,
            SkyDropdownMenuItem,
            SkyIcon,
            SkyPopoverMenu,
            SkyText,
            UserAvatar,
        },
        mixins: [toggle],
        props: {
            config: { type: Object as () => IDesktopUserInfoMenuConfig, default: () => {} },
        },
        data() {
            return {
                globalMessages: globalMessages,
                showExpandedMessage: false,
                sparkProgramName: this.config.sparkProgramName,
            };
        },
        computed: {
            expandedMessage() {
                return this.$formatMessage({
                    defaultMessage: "menu pop up expanded",
                    id: "globals.desktopUserInfoMenu.expandedMessage",
                });
            },
            menuTitle() {
                let title = this.$formatMessage(
                    {
                        defaultMessage: "Button to open user info menu for {userAlias}",
                        id: "globals.desktopUserInfoMenu.menuTitle",
                    },
                    { userAlias: this.config.user.alias },
                );
                if (this.config.hasNotifications) {
                    title += this.$formatMessage({
                        defaultMessage: ". Menu indicates user has notifications.",
                        id: "globals.desktopUserInfoMenu.menuNotifications",
                    });
                }

                return title;
            },
            notificationsText() {
                return this.$formatMessage(globalMessages.notifications);
            },
        },
        watch: {
            isOpen(newVal, oldVal) {
                if (newVal && !oldVal) {
                    this.showExpandedMessage = false;
                    this.$nextTick(() => {
                        this.showExpandedMessage = true;
                        setTimeout(() => {
                            this.showExpandedMessage = false;
                        }, 10000);
                    });
                }
            },
        },
        mounted() {
            globalEventHandler.on(GLOBAL_EVENTS.DESKTOP_MENU.RESIZE_TO_MOBILE, this.hideDesktopMenu);
        },
        beforeUnmount() {
            globalEventHandler.off(GLOBAL_EVENTS.DESKTOP_MENU.RESIZE_TO_MOBILE, this.hideDesktopMenu);
        },
        methods: {
            getPendoAttribute,
            hideDesktopMenu() {
                this.isOpen = false;
            },
            logout() {
                globalEventHandler.emit(GLOBAL_EVENTS.ACCOUNT.LOGOUT);
            },
            sparkLinkClick() {
                globalEventHandler.emit(GLOBAL_EVENTS.ANALYTICS.EVENT, { event: "spark_homepage" });
            },
        },
    };
</script>

<style lang="scss">
    @use "./DesktopUserInfoMenu.scss";
</style>
